import {useEffect, useRef} from 'react';
import {loadScript, toBase64} from 'coreUtils/utils';
import {isPayrailsRiskSDKActive} from 'coreUtils/payrailsRisk';
import {useCustomerMgr, useLocalesMgr, useConfigurations} from 'coreHooks';
import {useBasket} from 'brandHooks';
import {getConfig} from 'coreUtils/utils';

const SIGNIFYD_SCRIPT_URL = 'https://cdn-scripts.signifyd.com/api/script-tag.js';
export const SIGNIFYD_SCRIPT_ID = 'sig-api';

/**
 * Mirrors Signifyd.prototype.getOrderSessionId from the SFRA cartridge.
 * Produces a URL-safe base64 token from a truncated store URL + basket ID.
 */
export const buildOrderSessionId = (protocol: string, hostname: string, languageCode: string, basketId: string): string => {
	const storeURL = `${protocol}://${hostname}/${languageCode}/home`;
	const limitedLengthURL = storeURL.length > 50 ? storeURL.substring(0, 50) : storeURL;
	const base64 = toBase64(limitedLengthURL + basketId);

	return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

export interface SignifydApi {
	init: (cfg: {sessionId: string}) => void;
	pageView: () => void;
}

export const getSignifydApi = (): SignifydApi | undefined =>
	(window as unknown as {signifyd?: SignifydApi}).signifyd;

/**
 * useSignifydFingerprint custom hook.
 *
 * Loads the Signifyd device-fingerprinting script via `loadScript` and
 * binds the current order session ID.
 *
 * `loadScript` sets `data-order-session-id` on the `<script>` element
 * before appending it to the DOM, so the SDK auto-init finds it.
 * If the basket changes later, the hook re-initialises the SDK through
 * the explicit JS API (`signifyd.init`) without reloading the script.
 *
 * A `sigScriptError` listener acts as safety net: when the SDK fires
 * "No session ID found", the hook falls back to manual init + pageView.
 */
const useSignifydFingerprint = () => {
	const basket = useBasket();
	const localesMgr = useLocalesMgr();
	const customerMgr = useCustomerMgr();
	const configurationsMgr = useConfigurations();
	const config = getConfig();
	const currentOrderSessionId = useRef('');

	useEffect(() => {
		if (!basket.loaded || !customerMgr.isContextAvailable()) {
			return;
		}

		if (isPayrailsRiskSDKActive(configurationsMgr.get('sitePreferences.payrailsRiskMigrationConfig'))) {
			return;
		}

		const currentLocale = localesMgr.getCurrentLocale();
		const languageCode = currentLocale.getLanguageCode();
		const {instanceHostname, instanceProtocol} = config.app;
		const orderSessionId = buildOrderSessionId(instanceProtocol, instanceHostname, languageCode, basket.basketId);

		if (currentOrderSessionId.current === orderSessionId) {
			return;
		}

		currentOrderSessionId.current = orderSessionId;

		const existingTag = document.getElementById(SIGNIFYD_SCRIPT_ID);

		if (existingTag) {
			existingTag.setAttribute('data-order-session-id', orderSessionId);
		}

		const api = getSignifydApi();

		if (api) {
			api.init({sessionId: orderSessionId});

			return;
		}

		if (existingTag) {
			return;
		}

		let autoInitFailed = false;

		const handleScriptError = (event: Event) => {
			const detail: string = (event as CustomEvent).detail || '';

			if (detail.includes('No session ID found')) {
				autoInitFailed = true;
			}
		};

		document.addEventListener('sigScriptError', handleScriptError);

		loadScript(
			SIGNIFYD_SCRIPT_URL,
			SIGNIFYD_SCRIPT_ID,
			{'order-session-id': orderSessionId},
			true,
			{},
			{typeOverride: '', textContent: '', isHead: true, isReinsertable: false},
		)
			.then(() => {
				document.removeEventListener('sigScriptError', handleScriptError);

				if (!autoInitFailed) {
					return;
				}

				const sdkApi = getSignifydApi();

				if (sdkApi) {
					sdkApi.init({sessionId: orderSessionId});
					sdkApi.pageView();
				}
			})
			.catch(() => {
				document.removeEventListener('sigScriptError', handleScriptError);
			});

		return () => {
			document.removeEventListener('sigScriptError', handleScriptError);
		};
	}, [basket.loaded, basket.basketId, customerMgr.isContextAvailable()]);
};

export default useSignifydFingerprint;
